import {
  AspectRatio,
  Collapse,
  HStack,
  Icon,
  Link,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "../../customIcons";
import React, { useMemo, useState } from "react";

import AddToCartBundle from "../AddToCartBundle";
import { GatsbyImage } from "gatsby-plugin-image";
import { fetchAllProducts } from "../../../queries/fetchAllProducts";
import useAuth from "../../useAuth";

const CrossSellsProduct = ({
  bundles,
  productPrice,
  productId,
  isCart,
}: any) => {
  const { loggedIn, isUnlicensedUser, isCustomerDefault, isBankAcc } =
    useAuth();

  const allProducts = fetchAllProducts();

  const [expanded, setExpanded] = useState<{ [key: number]: boolean }>({});

  const handleToggle = (index: number) => {
    setExpanded((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const bundleData = useMemo(() => {
    if (!bundles) {
      return [];
    }

    const shouldFilterBundles =
      isUnlicensedUser || !loggedIn || isCustomerDefault || isBankAcc;

    const bundlesWithProducts = bundles?.map((bundle: any) => {
      const products = allProducts.filter((product: any) =>
        bundle.bundleProduct.some(
          (bundleProd: any) => bundleProd.productSku === product.sku,
        ),
      );

      const totalPrice = products.reduce((acc: number, product: any) => {
        const priceString = product?.price?.replace("$", "").trim();
        const price =
          priceString && !isNaN(parseFloat(priceString))
            ? parseFloat(priceString)
            : 0;
        return acc + price;
      }, 0);

      const baseProductPrice = productPrice
        ? parseFloat(productPrice.replace("$", "").trim())
        : 0;

      return {
        bundleName: bundle.bundleName,
        products,
        totalPrice: (totalPrice + baseProductPrice).toFixed(2),
      };
    });

    return shouldFilterBundles
      ? bundlesWithProducts.filter((bundle: any) => {
          return !bundle.products.some((product: any) => {
            return product.productTags.nodes.some(
              (node: any) => node.name === "Pharmaceuticals",
            );
          });
        })
      : bundlesWithProducts;
  }, [
    allProducts,
    bundles,
    isUnlicensedUser,
    loggedIn,
    isCustomerDefault,
    isBankAcc,
  ]);

  const isSingleBundle = bundles && bundles.length === 1;

  const getWidth = (length: any) => {
    if (length === 1) return "450px";
    if (length === 2) return isCart ? "237px" : "265px";
    return isCart ? "156px" : "175px";
  };

  return (
    <>
      {bundleData.map((bundle: any, bundleIndex: number) => {
        const productIds = bundle?.products?.map(
          (item: any) => item.databaseId,
        );

        if (!isCart) {
          productIds.push(productId);
        }

        return (
          <React.Fragment key={bundleIndex}>
            <HStack
              borderBottom="1px solid #E1E7EF"
              position="relative"
              onClick={() => !isSingleBundle && handleToggle(bundleIndex)}
              justifyContent="space-between"
              cursor={isSingleBundle ? "default" : "pointer"}
              p="10px"
              w="100%"
            >
              <HStack w="100%">
                <HStack>
                  {bundle.products.map((item: any) => {
                    const imageData =
                      item.featuredImage?.node?.localFile?.childImageSharp
                        ?.gatsbyImageData;
                    const altText = item.featuredImage?.node?.altText;
                    return (
                      <AspectRatio
                        key={item.databaseId}
                        border="1px solid #BB5D82"
                        borderRadius="6px"
                        mx="auto"
                        width="42px"
                        height="42px"
                        ratio={1 / 1}
                      >
                        <GatsbyImage
                          image={imageData}
                          alt={altText}
                          objectFit="cover"
                          style={{
                            height: "40px",
                            width: "40px",
                            borderRadius: "6px",
                          }}
                        />
                      </AspectRatio>
                    );
                  })}
                </HStack>
                <VStack
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  gap={1}
                  pl="4px"
                  alignItems="flex-start"
                  w="100%"
                  mr="20px"
                >
                  <Text color="#1A212D" fontWeight="600" fontSize="14px">
                    {bundle.bundleName.toUpperCase()}
                  </Text>
                  <Text
                    w="100%"
                    color="#4A5568"
                    fontSize="12px"
                    isTruncated
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {bundle.products
                      .map(
                        (item: any, index: number) =>
                          `${item.name}${
                            index < bundle.products.length - 1 ? ", " : ""
                          }`,
                      )
                      .join("")}
                  </Text>
                </VStack>
              </HStack>
              {!isSingleBundle && (
                <Icon
                  position="absolute"
                  right={2}
                  as={
                    expanded[bundleIndex]
                      ? MdKeyboardArrowUp
                      : MdKeyboardArrowDown
                  }
                  width="16px"
                  height="16px"
                  fill="#1A212D"
                />
              )}
            </HStack>

            <Collapse in={isSingleBundle || expanded[bundleIndex]}>
              <HStack
                bg="#f8fafc"
                w="100%"
                p="10px"
                borderRadius="6px"
                justifyContent="space-between"
                flexDir={{ base: "column", xl: "row" }}
              >
                <HStack
                  gap={1}
                  w="100%"
                  flexDir={{ base: "column", xl: "row" }}
                >
                  {bundle.products.map((item: any, index: any) => {
                    const width = getWidth(bundle?.products?.length);
                    const formattedPrice = `$${item.price.replace("$", "")}`;
                    const imageData =
                      item.featuredImage?.node?.localFile?.childImageSharp
                        ?.gatsbyImageData;
                    const altText = item.featuredImage?.node?.altText;
                    return (
                      <Link
                        key={index}
                        href={`/product/${item.slug}`}
                        isExternal
                        style={{ textDecoration: "none" }}
                        w={{ base: "100%", xl: width }}
                      >
                        <Stack
                          cursor="pointer"
                          key={item.databaseId}
                          flexDir="row"
                          bg="white"
                          borderRadius="8px"
                          border="1px solid #BB5D82"
                          gap={0}
                          alignItems="flex-start"
                        >
                          <AspectRatio
                            mx={{ base: "unset", xl: "auto" }}
                            width="62px"
                            height="62px"
                            ratio={1 / 1}
                            borderRadius="6px"
                          >
                            <GatsbyImage
                              image={imageData}
                              alt={altText}
                              objectFit="cover"
                              style={{
                                height: "60px",
                                width: "60px",
                                padding: 1,
                                borderRadius: "6px",
                              }}
                            />
                          </AspectRatio>
                          <VStack
                            alignItems="flex-start"
                            gap={0}
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            w="100%"
                            flex={1}
                          >
                            <Text
                              isTruncated
                              whiteSpace="nowrap"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              w="100%"
                              color="#1A212D"
                              pt="10px"
                              fontWeight="500"
                              pr={1}
                              fontSize="14px"
                            >
                              {item.name}
                            </Text>
                            <Text
                              fontSize="14px"
                              color="#1A212D"
                              fontWeight="600"
                            >
                              {formattedPrice}
                            </Text>
                          </VStack>
                        </Stack>
                      </Link>
                    );
                  })}
                </HStack>
                <HStack
                  pt={{ base: 0, xl: 2 }}
                  justifyContent="flex-end"
                  w="100%"
                >
                  <Text
                    ml="8px"
                    maxW={{ base: "100%", xl: "112px" }}
                    fontSize="12px"
                  >
                    Add {isCart ? "" : "this"}{" "}
                    {productIds.length === 1 ? "product" : "bundle"}{" "}
                    {isCart ? "to cart " : ""}
                    for ${bundle.totalPrice}
                  </Text>
                  <AddToCartBundle productIds={productIds} />
                </HStack>
              </HStack>
            </Collapse>
          </React.Fragment>
        );
      })}
    </>
  );
};

export default CrossSellsProduct;
